// Pure helpers for filtering / duplicate detection of admin user list.

export interface EnrichedProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  business_email: string | null;
  phone: string | null;
  whatsapp_number: string | null;
  account_type: string | null;
  organization_number: string | null;
  company_name: string | null;
  province: string | null;
  municipality: string | null;
  email_verified: boolean;
  admin_approved: boolean;
  avatar_url: string | null;
  cover_image_url: string | null;
  linkedin_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  skogsforum_url: string | null;
  created_at: string;
  updated_at: string;
  // Aggregates
  _activeListings: number;
  _reviewsCount: number;
  _avgRating: number;
  _completedPurchases: number;
  _warnings: number;
  _roles: string[];
  [key: string]: any;
}

export type IncludeExclude<T extends string = string> = {
  include: T[];
  exclude: T[];
};

export type RangeNum = { min: number | ""; max: number | "" };
export type TriState = "any" | "yes" | "no";
export type NameDup = "off" | "exact" | "fuzzy";

export interface AdvancedFilters {
  accountType: IncludeExclude<"personal" | "company" | "admin">;
  accountStatus: IncludeExclude<"active" | "restricted" | "pending" | "deleted">;
  verificationStatus: IncludeExclude<"verified" | "unverified" | "pending">;
  province: IncludeExclude;
  municipality: IncludeExclude;
  registeredFrom: string; // YYYY-MM-DD
  registeredTo: string;
  activeListings: RangeNum;
  warnings: RangeNum;
  completedPurchases: RangeNum;
  reviewsCount: RangeNum;
  avgRating: RangeNum;
  hasSocial: TriState;
  hasCover: TriState;
  hasAvatar: TriState;
  duplicates: {
    email: boolean;
    phone: boolean;
    organization_number: boolean;
    name: NameDup;
    kommun_name: boolean;
  };
}

export const defaultAdvancedFilters: AdvancedFilters = {
  accountType: { include: [], exclude: [] },
  accountStatus: { include: [], exclude: [] },
  verificationStatus: { include: [], exclude: [] },
  province: { include: [], exclude: [] },
  municipality: { include: [], exclude: [] },
  registeredFrom: "",
  registeredTo: "",
  activeListings: { min: "", max: "" },
  warnings: { min: "", max: "" },
  completedPurchases: { min: "", max: "" },
  reviewsCount: { min: "", max: "" },
  avgRating: { min: "", max: "" },
  hasSocial: "any",
  hasCover: "any",
  hasAvatar: "any",
  duplicates: {
    email: false,
    phone: false,
    organization_number: false,
    name: "off",
    kommun_name: false,
  },
};

// --- Derivations ---

function deriveAccountType(p: EnrichedProfile): "personal" | "company" | "admin" {
  if (p._roles.includes("admin")) return "admin";
  return (p.account_type as "personal" | "company") || "personal";
}

function deriveAccountStatus(p: EnrichedProfile): "active" | "restricted" | "pending" | "deleted" {
  // No dedicated status column; companies awaiting approval are "pending".
  if (p.account_type === "company" && !p.admin_approved) return "pending";
  return "active";
}

function deriveVerificationStatus(p: EnrichedProfile): "verified" | "unverified" | "pending" {
  if (p.account_type === "company") {
    if (p.email_verified && p.admin_approved) return "verified";
    if (p.email_verified || p.admin_approved) return "pending";
    return "unverified";
  }
  return p.email_verified ? "verified" : "unverified";
}

function hasSocial(p: EnrichedProfile): boolean {
  return !!(p.linkedin_url || p.instagram_url || p.facebook_url || p.skogsforum_url);
}

// --- Filter helpers ---

function passIncludeExclude<T extends string>(
  value: T | T[] | null | undefined,
  cfg: IncludeExclude<T>,
): boolean {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  if (cfg.exclude.length && values.some((v) => cfg.exclude.includes(v))) return false;
  if (cfg.include.length && !values.some((v) => cfg.include.includes(v))) return false;
  return true;
}

function passRange(value: number, r: RangeNum): boolean {
  if (r.min !== "" && value < Number(r.min)) return false;
  if (r.max !== "" && value > Number(r.max)) return false;
  return true;
}

function passTriBool(value: boolean, t: TriState): boolean {
  if (t === "any") return true;
  if (t === "yes") return value;
  return !value;
}

export function applyAdvancedFilters(
  profiles: EnrichedProfile[],
  f: AdvancedFilters,
): EnrichedProfile[] {
  return profiles.filter((p) => {
    if (!passIncludeExclude(deriveAccountType(p), f.accountType)) return false;
    if (!passIncludeExclude(deriveAccountStatus(p), f.accountStatus)) return false;
    if (!passIncludeExclude(deriveVerificationStatus(p), f.verificationStatus)) return false;
    if (!passIncludeExclude(p.province || "", f.province)) return false;
    if (!passIncludeExclude(p.municipality || "", f.municipality)) return false;

    if (f.registeredFrom && new Date(p.created_at) < new Date(f.registeredFrom)) return false;
    if (f.registeredTo) {
      const end = new Date(f.registeredTo);
      end.setHours(23, 59, 59, 999);
      if (new Date(p.created_at) > end) return false;
    }

    if (!passRange(p._activeListings, f.activeListings)) return false;
    if (!passRange(p._warnings, f.warnings)) return false;
    if (!passRange(p._completedPurchases, f.completedPurchases)) return false;
    if (!passRange(p._reviewsCount, f.reviewsCount)) return false;
    if (!passRange(p._avgRating, f.avgRating)) return false;

    if (!passTriBool(hasSocial(p), f.hasSocial)) return false;
    if (!passTriBool(!!p.cover_image_url, f.hasCover)) return false;
    if (!passTriBool(!!p.avatar_url, f.hasAvatar)) return false;

    return true;
  });
}

// --- Duplicate detection ---

export interface DuplicateGroup {
  label: string;
  value: string;
  users: EnrichedProfile[];
}

function normalizeStr(v: string | null | undefined): string {
  return (v || "").trim().toLowerCase();
}

function normalizePhone(v: string | null | undefined): string {
  return (v || "").replace(/[^0-9+]/g, "");
}

function normalizeOrg(v: string | null | undefined): string {
  return (v || "").replace(/[^0-9A-Za-z]/g, "").toLowerCase();
}

// Simple Levenshtein for fuzzy name matching
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const dp = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[b.length];
}

function groupExact<T>(
  items: T[],
  key: (t: T) => string,
  label: string,
): { label: string; value: string; users: T[] }[] {
  const map = new Map<string, T[]>();
  items.forEach((it) => {
    const k = key(it);
    if (!k) return;
    map.set(k, [...(map.get(k) || []), it]);
  });
  const groups: { label: string; value: string; users: T[] }[] = [];
  map.forEach((users, value) => {
    if (users.length > 1) groups.push({ label, value, users });
  });
  return groups;
}

function groupFuzzyNames(items: EnrichedProfile[]): DuplicateGroup[] {
  const cleaned = items.map((p) => ({ p, n: normalizeStr(p.full_name) })).filter((x) => x.n);
  const used = new Set<string>();
  const groups: DuplicateGroup[] = [];
  for (let i = 0; i < cleaned.length; i++) {
    if (used.has(cleaned[i].p.id)) continue;
    const cluster = [cleaned[i].p];
    used.add(cleaned[i].p.id);
    for (let j = i + 1; j < cleaned.length; j++) {
      if (used.has(cleaned[j].p.id)) continue;
      const a = cleaned[i].n;
      const b = cleaned[j].n;
      const maxLen = Math.max(a.length, b.length);
      const dist = levenshtein(a, b);
      // ≤2 edits OR ≤20% of max length
      if (dist <= 2 || dist / maxLen <= 0.2) {
        cluster.push(cleaned[j].p);
        used.add(cleaned[j].p.id);
      }
    }
    if (cluster.length > 1) {
      groups.push({ label: "Namn (fuzzy)", value: cleaned[i].n, users: cluster });
    }
  }
  return groups;
}

/**
 * Returns null when no duplicate criteria are active (regular list mode),
 * or an array of duplicate groups (duplicate mode).
 */
export function computeDuplicateGroups(
  profiles: EnrichedProfile[],
  d: AdvancedFilters["duplicates"],
): DuplicateGroup[] | null {
  const active =
    d.email || d.phone || d.organization_number || d.name !== "off" || d.kommun_name;
  if (!active) return null;

  const groups: DuplicateGroup[] = [];
  if (d.email) {
    groups.push(
      ...groupExact(
        profiles,
        (p) => normalizeStr(p.email) || normalizeStr(p.business_email),
        "E-post",
      ),
    );
  }
  if (d.phone) {
    groups.push(...groupExact(profiles, (p) => normalizePhone(p.phone), "Telefon"));
  }
  if (d.organization_number) {
    groups.push(
      ...groupExact(profiles, (p) => normalizeOrg(p.organization_number), "Org.nummer"),
    );
  }
  if (d.name === "exact") {
    groups.push(...groupExact(profiles, (p) => normalizeStr(p.full_name), "Namn (exakt)"));
  } else if (d.name === "fuzzy") {
    groups.push(...groupFuzzyNames(profiles));
  }
  if (d.kommun_name) {
    groups.push(
      ...groupExact(
        profiles,
        (p) => {
          const k = normalizeStr(p.municipality);
          const n = normalizeStr(p.full_name);
          return k && n ? `${k}|${n}` : "";
        },
        "Kommun + namn",
      ),
    );
  }
  return groups;
}
